'use client';

import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Divider,
  Link,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useConnectedAccounts } from '@/hooks/useConnectedAccounts';

type RepoRow = {
  name: string;
  fullName: string;
  htmlUrl: string;
  description: string | null;
  pushedAt: string | null;
  private: boolean;
};

type OrgRow = { login: string; avatarUrl: string | null; htmlUrl: string };

type Summary = {
  user: { login: string; htmlUrl: string; avatarUrl: string | null; name: string | null; publicRepos: number | null };
  repos: RepoRow[];
  orgs: OrgRow[];
  orgListForbidden?: boolean;
};

type Pin = { repoUrl: string; label?: string };

export function GitHubIdentityCard() {
  const { data: accounts } = useConnectedAccounts(true);
  const connected = Boolean(accounts?.github?.isConnected);

  const [summary, setSummary] = useState<Summary | null>(null);
  const [summaryError, setSummaryError] = useState<string | null>(null);
  const [loadingSummary, setLoadingSummary] = useState(false);

  const [pins, setPins] = useState<Pin[]>([]);
  const [pinDrafts, setPinDrafts] = useState<Pin[]>([{ repoUrl: '', label: '' }]);
  const [savingPins, setSavingPins] = useState(false);
  const [pinMessage, setPinMessage] = useState<string | null>(null);

  const loadSummary = useCallback(async () => {
    if (!connected) return;
    setLoadingSummary(true);
    setSummaryError(null);
    try {
      const r = await fetch('/api/github/summary', { credentials: 'include' });
      if (!r.ok) {
        const t = await r.text();
        setSummaryError(t || 'Could not load GitHub summary.');
        setSummary(null);
        return;
      }
      setSummary((await r.json()) as Summary);
    } catch {
      setSummaryError('Could not load GitHub summary.');
      setSummary(null);
    } finally {
      setLoadingSummary(false);
    }
  }, [connected]);

  const loadPins = useCallback(async () => {
    const r = await fetch('/api/user/github-pins', { credentials: 'include' });
    if (!r.ok) return;
    const j = (await r.json()) as { pins?: Pin[] };
    const list = Array.isArray(j.pins) ? j.pins : [];
    setPins(list);
    setPinDrafts(list.length > 0 ? list.map((p) => ({ repoUrl: p.repoUrl, label: p.label ?? '' })) : [{ repoUrl: '', label: '' }]);
  }, []);

  useEffect(() => {
    if (!connected) {
      setSummary(null);
      return;
    }
    void loadSummary();
    void loadPins();
  }, [connected, loadSummary, loadPins]);

  const savePins = async () => {
    setSavingPins(true);
    setPinMessage(null);
    try {
      const cleaned = pinDrafts
        .map((p) => ({
          repoUrl: p.repoUrl.trim(),
          label: p.label?.trim() || undefined,
        }))
        .filter((p) => p.repoUrl.length > 0);
      const r = await fetch('/api/user/github-pins', {
        method: 'PATCH',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ pins: cleaned }),
      });
      if (!r.ok) {
        const t = await r.text();
        setPinMessage(t || 'Save failed.');
        return;
      }
      setPinMessage('Saved.');
      await loadPins();
    } finally {
      setSavingPins(false);
    }
  };

  const readmeUrl = (repoHtml: string) => {
    try {
      const u = new URL(repoHtml);
      return `${u.origin}${u.pathname}#readme`;
    } catch {
      return repoHtml;
    }
  };

  if (!connected) {
    return null;
  }

  return (
    <Card variant="outlined" sx={{ mt: 2 }}>
      <CardContent>
        <Typography variant="h6" gutterBottom>
          GitHub (identity)
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
          Read-only summary from your connected GitHub token. Pin repo URLs to keep quick context for projects
          (no code hosting in FloAI).
        </Typography>

        {loadingSummary ? (
          <Typography variant="body2" color="text.secondary">
            Loading GitHub profile…
          </Typography>
        ) : summaryError ? (
          <Alert severity="warning">{summaryError}</Alert>
        ) : summary ? (
          <Box sx={{ mb: 2 }}>
            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap sx={{ mb: 1 }}>
              <Typography variant="subtitle1">{summary.user.name || summary.user.login}</Typography>
              <Chip size="small" label={`@${summary.user.login}`} />
              {summary.user.publicRepos != null ? (
                <Chip size="small" variant="outlined" label={`Public repos: ${summary.user.publicRepos}`} />
              ) : null}
            </Stack>
            <Button component={Link} href={summary.user.htmlUrl} target="_blank" rel="noopener noreferrer" size="small">
              Open GitHub profile
            </Button>

            {summary.orgListForbidden ? (
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                GitHub blocked listing organizations for this token (needs read:org). Disconnect and reconnect GitHub
                in Profile.
              </Typography>
            ) : null}

            {summary.orgs.length > 0 ? (
              <Box sx={{ mt: 2 }}>
                <Typography variant="subtitle2" gutterBottom>
                  Organizations
                </Typography>
                <Stack spacing={0.5}>
                  {summary.orgs.map((o) => (
                    <Link key={o.login} href={o.htmlUrl} target="_blank" rel="noopener noreferrer" variant="body2">
                      {o.login}
                    </Link>
                  ))}
                </Stack>
              </Box>
            ) : null}

            <Divider sx={{ my: 2 }} />

            <Typography variant="subtitle2" gutterBottom>
              Recent public repositories
            </Typography>
            <Stack spacing={1}>
              {summary.repos.slice(0, 8).map((r) => (
                <Box key={r.fullName}>
                  <Link href={r.htmlUrl} target="_blank" rel="noopener noreferrer" variant="body2" sx={{ fontWeight: 600 }}>
                    {r.fullName}
                  </Link>
                  {r.description ? (
                    <Typography variant="caption" color="text.secondary" display="block">
                      {r.description}
                    </Typography>
                  ) : null}
                  <Link href={readmeUrl(r.htmlUrl)} target="_blank" rel="noopener noreferrer" variant="caption">
                    README
                  </Link>
                </Box>
              ))}
            </Stack>
          </Box>
        ) : null}

        <Divider sx={{ my: 2 }} />

        <Typography variant="subtitle2" gutterBottom>
          Pinned repo links
        </Typography>
        <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
          Save up to five https://github.com/owner/repo links (for your own reference in FloAI).
        </Typography>
        {pins.length > 0 && !pinMessage ? (
          <Stack spacing={0.5} sx={{ mb: 1 }}>
            {pins.map((p) => (
              <Link key={p.repoUrl} href={p.repoUrl} target="_blank" rel="noopener noreferrer" variant="body2">
                {p.label || p.repoUrl}
              </Link>
            ))}
          </Stack>
        ) : null}
        {pinDrafts.map((row, idx) => (
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} key={idx} sx={{ mb: 1 }}>
            <TextField
              size="small"
              label="Repo URL"
              value={row.repoUrl}
              onChange={(e) => {
                const v = e.target.value;
                setPinDrafts((prev) => {
                  const next = [...prev];
                  next[idx] = { ...next[idx], repoUrl: v };
                  return next;
                });
              }}
              fullWidth
              placeholder="https://github.com/org/project"
            />
            <TextField
              size="small"
              label="Label (optional)"
              value={row.label ?? ''}
              onChange={(e) => {
                const v = e.target.value;
                setPinDrafts((prev) => {
                  const next = [...prev];
                  next[idx] = { ...next[idx], label: v };
                  return next;
                });
              }}
              sx={{ minWidth: { sm: 200 } }}
            />
          </Stack>
        ))}
        <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
          <Button
            size="small"
            variant="outlined"
            disabled={pinDrafts.length >= 5}
            onClick={() => setPinDrafts((p) => [...p, { repoUrl: '', label: '' }])}
          >
            Add row
          </Button>
          <Button size="small" variant="contained" onClick={() => void savePins()} disabled={savingPins}>
            Save pins
          </Button>
        </Stack>
        {pinMessage ? (
          <Typography variant="body2" color={pinMessage.startsWith('Saved') ? 'success.main' : 'error'} sx={{ mt: 1 }}>
            {pinMessage}
          </Typography>
        ) : null}
      </CardContent>
    </Card>
  );
}
